import { getOrCreateContentfulClient } from '@graphql/client'
import {
    GetArticleTopicList,
    getArticleTopicPage,
} from '@graphql/hooks/article-topic-page'
import {
    fetchContentfulPage,
    fetchContentfulPageMetaData,
} from '@lib/static/fetchStaticPage'
import { DEFAULT_PAGE_TTL } from '@lib/static/static-page'
import { PAGE_OPTIONS } from '@lib/utils/page'
import { ArticleTopicPageTemplate } from '@page-templates/article-topic-page'
import { isPrd } from '@shared/util/env'
import type { GetStaticPropsContext, NextPage } from 'next'

const ArticlesForTopic: NextPage = ArticleTopicPageTemplate

export const getStaticPaths = async () => {
    const client = getOrCreateContentfulClient(`/money-101`)

    // get all topics and generate static pages for them
    const { data } = await GetArticleTopicList(client)

    const paths = data?.articleTopicCollection?.items?.map((topic) => ({
        params: { slug: topic?.slug },
    }))

    return {
        paths: isPrd() ? paths : [],
        fallback: 'blocking',
    }
}

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
    try {
        const slug = params?.slug

        if (typeof slug !== 'string') {
            throw new Error('No slug provided')
        }

        const client = getOrCreateContentfulClient(`/money-101/${slug}`)

        const { data } = await getArticleTopicPage(client, {
            variables: { slug },
        })

        const topic = data?.topics?.items?.[0]

        if (!topic) {
            throw new Error(`No topic found for slug: ${slug}`)
        }

        const page = await fetchContentfulPage(
            PAGE_OPTIONS.MONEY_101_TOPIC.slug,
        )

        const metaData = topic.meta?.sys?.id
            ? await fetchContentfulPageMetaData(client, topic.meta?.sys?.id)
            : topic.meta

        return {
            props: {
                title: topic.title,
                articles: {
                    items: topic.linkedFrom?.articles?.items,
                },
                meta: metaData ?? null,
                page,
            },
            revalidate: DEFAULT_PAGE_TTL,
        }
    } catch (error) {
        return { notFound: true }
    }
}

export default ArticlesForTopic
